import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiOriginalsQuestion } from '@wix/da-papi-types';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowUp';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import EditIcon from '@wix/da-ds/pkg/Icons/16x16/Edit';
import TrashIcon from '@wix/da-ds/pkg/Icons/16x16/Trashcan';
import QuestionDisplay from '@wix/da-ds/pkg/originals/QuestionDisplay';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import QuestionViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/OriginalsQuestion';
import AnswerViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/OriginalsAnswer';
import { UnsavedQuestion } from '../../../../types';
import { DuperbrowseDisabled } from '@wix/da-shared-react/pkg/Duperbrowse/DuperbrowseDisabledContext';

import s from './QuestionCard.scss';

interface Props {
  question: PapiOriginalsQuestion | UnsavedQuestion;
  hasMoveUp: boolean;
  hasMoveDown: boolean;
  onMoveUpClick: () => void;
  onMoveDownClick: () => void;
  onEditClick: () => void;
  onRemoveClick: () => void;
  disableActions?: boolean;
}

const QuestionCard: React.FC<Props> = ({
  question,
  hasMoveUp,
  hasMoveDown,
  onMoveUpClick,
  onMoveDownClick,
  onEditClick,
  onRemoveClick,
  disableActions,
}) => {
  const { t } = useTranslation();
  return (
    <div className={s['root']}>
      <div className={s['actions']}>
        <div className={s['move-actions']}>
          {!disableActions && (
            <>
              <IconButton
                aria-labelledby="move-up"
                tooltipText={t('originalDeviationPopup.questionnaire.actions.moveUp')}
                size="medium"
                variant="edit-purple"
                icon={ArrowUpIcon}
                onClick={onMoveUpClick}
                className={c(!hasMoveUp && s['move-action-disabled'])}
                disabled={!hasMoveUp}
              />
              <IconButton
                aria-labelledby="move-down"
                tooltipText={t('originalDeviationPopup.questionnaire.actions.moveDown')}
                size="medium"
                variant="edit-purple"
                icon={ArrowDownIcon}
                onClick={onMoveDownClick}
                className={c(!hasMoveDown && s['move-action-disabled'])}
                disabled={!hasMoveDown}
              />
            </>
          )}
        </div>
        <div className={s['side-actions']}>
          <TextButton
            size="small"
            variant="edit-purple"
            icon={EditIcon}
            onClick={onEditClick}
            disabled={disableActions}
          >
            {t('common.edit')}
          </TextButton>
          <TextButton
            size="small"
            variant="edit-purple"
            icon={TrashIcon}
            onClick={onRemoveClick}
            disabled={disableActions}
          >
            {t('common.delete')}
          </TextButton>
        </div>
      </div>
      <hr className={s['divider']} />
      <QuestionDisplay
        question={question}
        editorRenderer={(body, type) => (
          <DuperbrowseDisabled>
            <TextRenderer
              textContent={body}
              tipTapViewer={type === 'question' ? QuestionViewer : AnswerViewer}
            />
          </DuperbrowseDisabled>
        )}
      />
    </div>
  );
};
QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;
